import { FC, useEffect, useState } from 'react';
import { useSetAtom } from 'jotai';
import { useForm } from '@tanstack/react-form';
import { useStore } from '@tanstack/react-store';
import { SidePanel } from '@carbon/ibm-products';
import { InlineNotification, TextInput } from '@carbon/react';
import type { SharedWellEditPanelProps } from 'pages/documents/structuring/well-review-match';
import type { StagedWell } from './types-well';
import { editWellAtom } from './atoms/wells';
import wellStyles from '../../afe-catalog/structuring/wells.module.scss';

// RC staged-well edit SidePanel. Clone of DO's well-edit-panel, re-scoped to the
// RC controller's editable blob keys. RC staged wells carry wellName / uwiApi /
// county / state / countyState / legalDescription / operator / propertyNumber /
// propertyName (no DO division_order_number) — the editable form fields are the
// same set DO exposes. PATCHes .../royalty_check_structuring/wells/:id and hosts
// the SidePanel on `.rc-structuring-shell`.
//
// The staged fields map onto the RoyaltyCheckTarget `data` blobs the RC
// _staged_well jbuilder reads back:
//   Well Name         → well_name (scalar)
//   UWI / API Number  → identifiers.uwi
//   County / State    → location_summary.{county, state}
//   Operator          → additional_header_fields.operator
//   Legal Description → legal_descriptions[0].legal_description
//
// WellMatchable#edit_staged_fields! DEEP-MERGES the submitted leaves into the existing `data` and
// treats a null/blank leaf as "clear that leaf". So this panel sends the FULL
// set of managed leaves on every save (value or `null`) — never a subset: `null`
// lets the user clear a wrongly-extracted value, and the deep-merge preserves
// unmanaged siblings the FE doesn't edit.

interface FormState {
  wellName: string;
  uwiApi: string;
  county: string;
  state: string;
  operator: string;
  legalDescription: string;
}

const blank = (value: string) => (value.trim() === '' ? null : value.trim());

// Pre-fill from the RC staged-well response. County/State arrive both combined
// (`countyState`) and split (`county`/`state`) — prefer the split fields.
const buildFormValues = (staged: StagedWell): FormState => ({
  wellName: staged.wellName ?? '',
  uwiApi: staged.uwiApi ?? '',
  county: staged.county ?? '',
  state: staged.state ?? '',
  operator: staged.operator ?? '',
  legalDescription: staged.legalDescription ?? '',
});

export const WellEditPanel: FC<SharedWellEditPanelProps<StagedWell>> = ({
  open,
  documentId,
  well,
  staged,
  editHref,
  onClose,
  onSaved,
}) => {
  const editWell = useSetAtom(editWellAtom);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm({
    defaultValues: buildFormValues(staged),
    onSubmit: ({ value }) => {
      const url =
        editHref ??
        `/documents/${documentId}/royalty_check_structuring/wells/${well.id}`;

      // Send the full set of managed leaves every save, `null` for any blanked
      // field, so the backend deep-merge can distinguish "unchanged" from
      // "cleared" and preserve unmanaged siblings.
      const wellPayload = {
        well_name: blank(value.wellName),
        identifiers: { uwi: blank(value.uwiApi) },
        location_summary: {
          county: blank(value.county),
          state: blank(value.state),
        },
        additional_header_fields: { operator: blank(value.operator) },
        legal_descriptions: [
          { legal_description: blank(value.legalDescription) },
        ],
      };

      setPending(true);
      setError(null);
      editWell({
        url,
        method: 'PATCH',
        data: { well: wellPayload },
        onSuccess: () => {
          setPending(false);
          onSaved();
        },
        onError: (m) => {
          setPending(false);
          setError(m ?? 'Failed to save well.');
        },
      });
    },
  });

  // Re-seed the form whenever the panel (re)opens for a well.
  useEffect(() => {
    if (open) {
      form.reset(buildFormValues(staged));
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, well.id]);

  // Live well name drives the panel title (updates as the user edits the field).
  const wellNameValue = useStore(form.store, (s) => s.values.wellName);

  return (
    <SidePanel
      open={open}
      onRequestClose={onClose}
      title={wellNameValue || staged.wellName || 'Edit Well'}
      subtitle="Edit the staged well details extracted from the document."
      className={wellStyles.panelLightFields}
      slideIn
      selectorPageContent=".rc-structuring-shell"
      placement="right"
      size="md"
      actions={[
        { kind: 'secondary' as const, label: 'Cancel', onClick: onClose },
        {
          kind: 'primary' as const,
          label: pending ? 'Saving…' : 'Save Changes',
          disabled: pending,
          onClick: () => form.handleSubmit(),
        },
      ]}
    >
      {open && (
        <div className={wellStyles.editGrid}>
          {error && (
            <InlineNotification
              kind="error"
              lowContrast
              title="Error"
              subtitle={error}
              onCloseButtonClick={() => setError(null)}
            />
          )}
          <form.Field name="wellName">
            {(field) => (
              <TextInput
                id="rc-well-edit-name"
                labelText="Well Name"
                size="sm"
                value={field.state.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.handleChange(e.target.value)
                }
                light
              />
            )}
          </form.Field>
          <form.Field name="uwiApi">
            {(field) => (
              <TextInput
                id="rc-well-edit-uwi-api"
                labelText="UWI / API Number"
                size="sm"
                value={field.state.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.handleChange(e.target.value)
                }
                light
              />
            )}
          </form.Field>
          <form.Field name="county">
            {(field) => (
              <TextInput
                id="rc-well-edit-county"
                labelText="County"
                size="sm"
                value={field.state.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.handleChange(e.target.value)
                }
                light
              />
            )}
          </form.Field>
          <form.Field name="state">
            {(field) => (
              <TextInput
                id="rc-well-edit-state"
                labelText="State"
                size="sm"
                value={field.state.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.handleChange(e.target.value)
                }
                light
              />
            )}
          </form.Field>
          <form.Field name="operator">
            {(field) => (
              <TextInput
                id="rc-well-edit-operator"
                labelText="Operator"
                size="sm"
                value={field.state.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.handleChange(e.target.value)
                }
                light
              />
            )}
          </form.Field>
          <form.Field name="legalDescription">
            {(field) => (
              <TextInput
                id="rc-well-edit-legal-description"
                labelText="Legal Description"
                size="sm"
                value={field.state.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.handleChange(e.target.value)
                }
                light
              />
            )}
          </form.Field>
        </div>
      )}
    </SidePanel>
  );
};
